import DescriptionIcon from '@material-design-icons/svg/filled/description.svg?react';
import EmojiEventsIcon from '@material-design-icons/svg/outlined/emoji_events.svg?react';
import HomeIcon from '@material-design-icons/svg/filled/home.svg?react';
import KeyboardArrowRightIcon from '@material-design-icons/svg/filled/keyboard_arrow_right.svg?react';
import StarBorderIcon from '@material-design-icons/svg/outlined/star.svg?react';
import type { Dapp } from '@src/dto/Dapp';
import { DappNavButton } from '../DappNavButton';
import { S3Image } from '../S3Image';
import {
  getDappHomePath,
  getDappDetailsPath,
  getDappReviewsPath,
  getDappQuestsPath,
  getDappPathBySegment,
  getDappRouteSegment,
} from '../../utils/dappRoutes';

const DAPP_NAV_ITEMS = [
  {
    to: '/home',
    icon: <HomeIcon />,
    label: 'Home',
  },
  {
    to: '/details',
    icon: <DescriptionIcon />,
    label: 'Details',
  },
  {
    to: '/reviews',
    icon: <StarBorderIcon />,
    label: 'Ratings & Reviews',
  },
];

// Surfaced only for dapps that participate in at least one challenge quest
// (see Dapp.hasQuests), so it sits apart from the always-present items above.
const QUESTS_NAV_ITEM = {
  to: '/quests',
  icon: <EmojiEventsIcon />,
  label: 'Quests',
};

export interface DappsSectionProps {
  title?: string;
  dapps: Dapp[];
  expandedDappId: string | null;
  onDappToggle: (dappId: string | null) => void;
  isActiveRoute: (path: string) => boolean;
  onLinkClick?: () => void;
}

export const DappsSection = ({
  title = 'DAPPS',
  dapps,
  expandedDappId,
  onDappToggle,
  isActiveRoute,
  onLinkClick,
}: DappsSectionProps) => {
  if (dapps.length === 0) return null;

  return (
    <div className="px-2 pt-4 pb-2 flex flex-col justify-start items-start gap-2 w-full">
      <div className="w-40 h-5 relative px-2">
        <div className="opacity-70 text-white text-xs font-semibold uppercase leading-tight tracking-tight">
          {title}
        </div>
      </div>

      <div className="flex flex-col justify-start items-start w-full">
        {dapps.map((dapp) => (
          <div key={dapp.id} className="w-full">
            <div
              className="w-60 h-9 px-2 py-3.5 rounded inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-800/50 transition-colors"
              onClick={() =>
                onDappToggle(expandedDappId === dapp.id ? null : dapp.id)
              }
            >
              <div className="flex justify-start items-center gap-1">
                <div
                  className={`transform transition-transform duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)] ${
                    expandedDappId === dapp.id ? 'rotate-90' : 'rotate-0'
                  }`}
                >
                  <KeyboardArrowRightIcon
                    className="size-4"
                    style={{ fill: 'white' }}
                  />
                </div>
              </div>
              <div className="rounded-full flex justify-start items-center gap-2">
                <div className="size-5 relative flex items-center justify-center bg-neutral-800 rounded-lg overflow-hidden">
                  {dapp.dappIconUrl ? (
                    <S3Image
                      src={dapp.dappIconUrl}
                      thumbnailSrc={dapp.dappIconThumbnailUrl}
                      alt=""
                      aria-hidden="true"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-600 text-xs">
                      {dapp.dappName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 text-white text-sm font-bold leading-tight">
                {dapp.dappName}
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                expandedDappId === dapp.id
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pl-4 py-1 space-y-1">
                {(dapp.hasQuests
                  ? [...DAPP_NAV_ITEMS, QUESTS_NAV_ITEM]
                  : DAPP_NAV_ITEMS
                ).map((item, index) => {
                  const segment = getDappRouteSegment(dapp);
                  const to =
                    item.to === '/home'
                      ? getDappHomePath(dapp)
                      : item.to === '/details'
                        ? getDappDetailsPath(dapp)
                        : item.to === '/reviews'
                          ? getDappReviewsPath(dapp)
                          : item.to === '/quests'
                            ? getDappQuestsPath(dapp)
                            : getDappPathBySegment(segment, item.to);
                  const releaseBasePath = getDappPathBySegment(
                    segment,
                    '/release',
                  );

                  return (
                    <div
                      key={`${dapp.id}-${item.to}`}
                      className={`transition-opacity duration-300 ease-out ${
                        expandedDappId === dapp.id ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{
                        transitionDelay:
                          expandedDappId === dapp.id
                            ? `${index * 50}ms`
                            : '0ms',
                      }}
                    >
                      <DappNavButton
                        to={to}
                        icon={item.icon}
                        label={item.label}
                        isActive={
                          item.to === '/home'
                            ? isActiveRoute(getDappHomePath(dapp)) ||
                              isActiveRoute(releaseBasePath)
                            : isActiveRoute(to)
                        }
                        showDraftBadge={
                          item.to === '/details' &&
                          dapp.status === 'Draft' &&
                          (dapp.releaseCount === undefined ||
                            dapp.releaseCount === 0)
                        }
                        onClick={onLinkClick}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
